import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LiveLayout from "./LiveLayout";
import api from "@/lib/api";
import EntityLogo from "@/components/EntityLogo";
import { useAuth } from "@/contexts/AuthContext";
import { CalendarDays, Shield, ChevronRight, Radio } from "lucide-react";

export default function LiveSchedule() {
  const { user } = useAuth();
  const [feed, setFeed] = useState(null);

  useEffect(() => { api.get("/live/feed").then(({ data }) => setFeed(data)).catch(() => setFeed({})); }, []);

  const live = feed?.live_games || [];
  const upcoming = feed?.upcoming_games || [];
  const finals = feed?.final_scores || [];

  return (
    <LiveLayout>
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-3xl mx-auto px-5 py-7">
          <div className="label-overline text-[#E63946] flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Schedule</div>
          <h1 className="font-display text-3xl uppercase tracking-tight mt-1">Games Across CBR</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-6 space-y-8" data-testid="live-schedule">
        {!feed ? <div className="py-16 text-center text-sm text-slate-400">Loading…</div> : <>
          {!user && <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-500">Sign in to follow teams and see your games first.</div>}
          <Group title="Live Now" items={live} kind="live" empty="No games are live." />
          <Group title="Upcoming" items={upcoming} kind="upcoming" empty="No upcoming games scheduled." />
          <Group title="Recent Results" items={finals} kind="final" empty="No completed games yet." />
        </>}
      </div>
    </LiveLayout>
  );
}

function Group({ title, items, kind, empty }) {
  return (
    <section data-testid={`schedule-group-${kind}`}>
      <h2 className="font-display text-lg uppercase tracking-tight text-[#0A192F] mb-3">{title}</h2>
      {items.length === 0 ? <div className="bg-white border border-dashed border-slate-200 rounded-lg py-6 text-center text-sm text-slate-400">{empty}</div>
        : <div className="space-y-2">{items.map((g) => <Row key={g.id} g={g} kind={kind} />)}</div>}
    </section>
  );
}
function Row({ g, kind }) {
  const showScore = kind !== "upcoming";
  const isHome = g.home_team_id === g.team_id;
  const neutral = g.neutral_site || g.home_away === "neutral";
  const locLabel = neutral ? "Neutral" : isHome ? "Home" : "Away";
  return (
    <div data-testid={`schedule-game-row-${g.id}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3 hover:border-[#E63946] transition-colors group">
      <div className="w-14 text-center shrink-0">
        {kind === "live" ? <span className="text-[9px] font-bold text-white bg-[#E63946] px-1.5 py-0.5 rounded-sm animate-pulse">LIVE</span>
          : kind === "final" ? <span className="text-[10px] text-slate-500 font-semibold">FINAL</span>
          : <span className="text-[11px] text-slate-400">{(g.date || "").slice(5)}</span>}
      </div>
      <div className="flex-1 min-w-0 space-y-0.5">
        <Line name={g.team?.name} slug={g.team?.slug} logo={g.team?.logo_url} score={g.score?.home} show={showScore} />
        <Line name={g.opponent?.name} slug={g.opponent?.slug} logo={g.opponent?.logo_url} score={g.score?.away} show={showScore} />
        <div className="flex items-center gap-1.5 pt-0.5 text-[10px] text-slate-400 flex-wrap">
          <span className="capitalize">{(g.game_type || "").replace(/_/g, " ")}</span>
          <span className="text-slate-300">·</span><span>{locLabel}</span>
          {kind === "final" && g.outcome && <span className={`font-bold ${g.outcome === "W" ? "text-emerald-600" : g.outcome === "L" ? "text-[#E63946]" : "text-slate-500"}`}>· {g.outcome}</span>}
          {g.quality_win && <span className="inline-flex items-center gap-0.5 text-[#E63946] font-bold">· ★ Quality Win</span>}
        </div>
      </div>
      <Link to={`/live/games/${g.id}`} className="shrink-0"><ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#E63946]" /></Link>
    </div>
  );
}
const Line = ({ name, slug, logo, score, show }) => (
  <div className="flex items-center gap-2">
    <EntityLogo src={logo} alt={name} size="w-5 h-5" textSize="text-[7px]" />
    {slug
      ? <Link to={`/live/teams/${slug}`} className="text-sm truncate flex-1 text-slate-700 hover:text-[#E63946] font-medium">{name || "TBD"}</Link>
      : <span className="text-sm truncate flex-1 text-slate-700">{name || "TBD"}</span>}
    {show && <span className="font-data text-slate-500">{score ?? 0}</span>}
  </div>
);
